/**
 * Miroir TS du helper PHP `api/email_template_render.php`.
 * Sert à la prévisualisation live côté frontend (settings + composer).
 * Le rendu côté serveur reste autoritatif au moment de l'envoi.
 *
 * Syntaxes supportées :
 *   - {{lead.firstName}}, {{user.email}}, {{company.name}}, {{date.today}}, {{lead.custom.<key>}}
 *   - {nom}, {email}, {entreprise}  (alias rétrocompatibles)
 */

import type { Lead, User, Company } from '../types';

export interface EmailTemplateContext {
  /** Map plat clé canonique en lower-case → valeur. */
  values: Record<string, string>;
}

/** Catégorie d'une variable, utilisée pour grouper la palette dans l'UI. */
export type EmailVariableGroup = 'lead' | 'user' | 'company' | 'date';

export interface EmailVariableDef {
  /** Clé canonique sans accolades, en lower-case (ex: "lead.firstname"). */
  key: string;
  /** Forme d'insertion dans l'éditeur (ex: "{{lead.firstName}}"). */
  token: string;
  /** Libellé affiché dans la palette. */
  label: string;
  group: EmailVariableGroup;
  /** Description courte / exemple. */
  example?: string;
}

/**
 * Catalogue des variables disponibles. Source de vérité pour l'UI (palette + suggestions)
 * et pour les warnings "variable non résolue".
 */
export const EMAIL_TEMPLATE_VARIABLES: ReadonlyArray<EmailVariableDef> = [
  // Lead
  { key: 'lead.firstname',  token: '{{lead.firstName}}', label: 'Prénom du prospect',     group: 'lead', example: 'Jean' },
  { key: 'lead.lastname',   token: '{{lead.lastName}}',  label: 'Nom du prospect',        group: 'lead', example: 'Dupont' },
  { key: 'lead.fullname',   token: '{{lead.fullName}}',  label: 'Nom complet',            group: 'lead', example: 'Jean Dupont' },
  { key: 'lead.email',      token: '{{lead.email}}',     label: 'Email du prospect',      group: 'lead', example: 'jean@acme.fr' },
  { key: 'lead.phone',      token: '{{lead.phone}}',     label: 'Téléphone',              group: 'lead', example: '06 12 34 56 78' },
  { key: 'lead.company',    token: '{{lead.company}}',   label: 'Société du prospect',    group: 'lead', example: 'ACME SAS' },
  { key: 'lead.status',     token: '{{lead.status}}',    label: 'Statut du prospect',     group: 'lead', example: 'qualified' },
  { key: 'lead.sector',     token: '{{lead.sector}}',    label: 'Secteur d’activité',     group: 'lead', example: 'BTP' },
  { key: 'lead.tag',        token: '{{lead.tag}}',       label: 'Tag',                    group: 'lead', example: 'VIP' },
  { key: 'lead.revenue',    token: '{{lead.revenue}}',   label: 'Revenu potentiel',       group: 'lead', example: '50-100K' },

  // Sender
  { key: 'user.firstname',  token: '{{user.firstName}}', label: 'Mon prénom',             group: 'user' },
  { key: 'user.lastname',   token: '{{user.lastName}}',  label: 'Mon nom',                group: 'user' },
  { key: 'user.fullname',   token: '{{user.fullName}}',  label: 'Mon nom complet',        group: 'user' },
  { key: 'user.email',      token: '{{user.email}}',     label: 'Mon email',              group: 'user' },
  { key: 'user.signature',  token: '{{user.signature}}', label: 'Ma signature',           group: 'user' },

  // Company (tenant)
  { key: 'company.name',    token: '{{company.name}}',    label: 'Nom de mon entreprise', group: 'company' },
  { key: 'company.phone',   token: '{{company.phone}}',   label: 'Téléphone entreprise',  group: 'company' },
  { key: 'company.email',   token: '{{company.email}}',   label: 'Email entreprise',      group: 'company' },
  { key: 'company.website', token: '{{company.website}}', label: 'Site web',              group: 'company' },

  // Date
  { key: 'date.today',      token: '{{date.today}}',     label: "Date du jour",           group: 'date', example: '09/05/2026' },
  { key: 'date.tomorrow',   token: '{{date.tomorrow}}',  label: 'Demain',                 group: 'date' },
  { key: 'date.year',       token: '{{date.year}}',      label: 'Année courante',         group: 'date', example: '2026' },
];

const GROUP_LABEL: Record<EmailVariableGroup, string> = {
  lead: 'Prospect',
  user: 'Expéditeur',
  company: 'Mon entreprise',
  date: 'Date',
};

export function emailVariableGroupLabel(g: EmailVariableGroup): string {
  return GROUP_LABEL[g];
}

const splitFullName = (full?: string | null): { first: string; last: string } => {
  const n = (full || '').trim();
  if (!n) return { first: '', last: '' };
  const parts = n.split(/\s+/);
  if (parts.length === 1) return { first: parts[0], last: '' };
  const [first, ...rest] = parts;
  return { first, last: rest.join(' ') };
};

const formatDateFr = (d: Date): string => {
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy}`;
};

/**
 * Construit le contexte côté front à partir des objets en mémoire (live preview).
 * Le serveur reconstruit le sien depuis la DB lors de l'envoi (autoritatif).
 */
export function buildEmailTemplateContext(args: {
  lead?: Lead | null;
  user?: User | null;
  company?: Company | null;
  signature?: string | null;
  now?: Date;
}): EmailTemplateContext {
  const values: Record<string, string> = {};
  const now = args.now ?? new Date();

  if (args.lead) {
    const { first, last } = splitFullName(args.lead.name);
    values['lead.fullname']  = args.lead.name || '';
    values['lead.firstname'] = first;
    values['lead.lastname']  = last;
    values['lead.email']     = args.lead.email || '';
    values['lead.phone']     = args.lead.phone || '';
    values['lead.status']    = args.lead.status || '';
    values['lead.sector']    = args.lead.sectorActivity || '';
    values['lead.tag']       = args.lead.tag || '';
    values['lead.company']   = args.lead.clientCompanyName || '';
    values['lead.revenue']   = args.lead.potentialRevenue || '';
    if (args.lead.customFields) {
      for (const [k, v] of Object.entries(args.lead.customFields)) {
        if (v == null) continue;
        if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') {
          values['lead.custom.' + k.toLowerCase()] = String(v);
        }
      }
    }
  }

  if (args.user) {
    const { first, last } = splitFullName(args.user.name);
    values['user.fullname']  = args.user.name || '';
    values['user.firstname'] = first;
    values['user.lastname']  = last;
    values['user.email']     = args.user.email || '';
  }
  if (args.signature) {
    values['user.signature'] = args.signature;
  } else if (args.user?.name) {
    values['user.signature'] = args.user.name;
  }

  if (args.company) {
    values['company.name']     = args.company.name || '';
    values['company.currency'] = args.company.currency || '';
    values['company.phone']    = (args.company as any).phone || '';
    values['company.email']    = (args.company as any).email || '';
    values['company.website']  = (args.company as any).website || '';
  }

  const tomorrow = new Date(now.getTime() + 24 * 60 * 60 * 1000);
  values['date.today']    = formatDateFr(now);
  values['date.tomorrow'] = formatDateFr(tomorrow);
  values['date.iso']      = now.toISOString().slice(0, 10);
  values['date.year']     = String(now.getFullYear());

  return { values };
}

const LEGACY_ALIASES: Record<string, string> = {
  nom: 'lead.firstname',
  email: 'lead.email',
  entreprise: 'lead.company',
};

/**
 * Remplace les variables d'un template par leur valeur depuis $context.
 * Variables non résolues laissées intactes (pour détection downstream).
 */
export function renderEmailTemplate(tpl: string, ctx: EmailTemplateContext): string {
  if (!tpl) return '';
  let out = tpl;

  // 1) Aliases rétrocompatibles {nom} / {email} / {entreprise}
  for (const [legacy, canonical] of Object.entries(LEGACY_ALIASES)) {
    const v = ctx.values[canonical];
    if (v == null) continue;
    out = out.replace(new RegExp('\\{\\s*' + legacy + '\\s*\\}', 'gi'), v);
  }

  // 2) Forme canonique {{key.path}}
  out = out.replace(/\{\{\s*([A-Za-z][A-Za-z0-9_.]*)\s*\}\}/g, (m, rawKey) => {
    const k = String(rawKey).toLowerCase();
    const v = ctx.values[k];
    return v != null ? v : m;
  });

  return out;
}

/**
 * Liste les variables encore non résolues (forme {{...}}) après rendu.
 * Utilisé pour avertir l'utilisateur avant l'envoi.
 */
export function findUnresolvedEmailPlaceholders(rendered: string): string[] {
  const found = new Set<string>();
  const re = /\{\{\s*([A-Za-z][A-Za-z0-9_.]*)\s*\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(rendered)) !== null) {
    found.add(m[1]);
  }
  return Array.from(found);
}

/**
 * Insère une variable à la position du curseur d'un input/textarea, ou à la fin.
 * Retourne le nouveau texte + la nouvelle position de curseur.
 */
export function insertVariableAtCursor(
  current: string,
  token: string,
  selectionStart: number | null,
  selectionEnd: number | null
): { value: string; cursor: number } {
  const start = selectionStart ?? current.length;
  const end   = selectionEnd ?? current.length;
  const value = current.slice(0, start) + token + current.slice(end);
  return { value, cursor: start + token.length };
}
